import React, { useState, useEffect } from "react";
import { toastr } from 'react-redux-toastr';
import {
	Button, FormGroup, Input,
	Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import general_fetch from '../../utils/fetch';

// Helper: derive percentage-applied from order numbers in case the column is
// missing (older rows). After the migration runs, `due.tds_percentage` is
// authoritative; this is just a fallback for the first deploy and any read
// path that hasn't been refreshed yet.
export const derive_tds_percentage = (due) => {
	if (!due) return 0;
	if (due.tds_percentage !== undefined && due.tds_percentage !== null) {
		return Number(due.tds_percentage) || 0;
	}
	if (!due.tds || !due.price_after_discount_before_tax) return 0;
	return Math.round((Number(due.tds) / Number(due.price_after_discount_before_tax)) * 100 * 100) / 100;
};

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/**
 * Modal for CS to apply / change / remove TDS on a postpaid (renewal/overage)
 * or mid-cycle prepaid order. Used from the Dues tab and from the Mid-Cycle
 * Payments modal.
 *
 * TDS can be entered as a percentage rate OR a fixed (absolute) amount — toggled
 * by the "Percentage / Fixed amount" switch — mirroring how a discount can be
 * absolute or percentage. The two modes are mutually exclusive on the backend.
 *
 * Props:
 *  - open, toggle: standard reactstrap Modal control.
 *  - due: the order being edited (must include id, tds, tds_percentage, tds_absolute,
 *    price_after_discount_before_tax, tax — used for the breakup preview).
 *  - store_info: org details (used for currency symbol).
 *  - set_page_loader: existing global loader.
 *  - on_success: callback invoked after a successful apply_tds call. Each caller
 *    refetches its own list via this hook (Dues tab refetches dues; Mid-Cycle
 *    refetches pending prepaid orders). Optional.
 */
const EditTdsModal = ({ open, toggle, due, store_info, set_page_loader, on_success }) => {
	const PRESETS = [
		{ value: 0, label: 'No TDS (0%)' },
		{ value: 2, label: 'Technical fees (2%)' },
		{ value: 10, label: 'Professional fees (10%)' },
		{ value: 'custom', label: 'Custom...' },
	];

	const current_percentage = derive_tds_percentage(due);
	const current_absolute = Number(due && due.tds_absolute) || 0;
	const padbt = Number(due && due.price_after_discount_before_tax || 0);

	// 'percentage' | 'absolute'
	const [mode, set_mode] = useState('percentage');
	const [selected, set_selected] = useState(null);
	const [custom_value, set_custom_value] = useState('');
	const [absolute_value, set_absolute_value] = useState('');
	const [submitting, set_submitting] = useState(false);

	useEffect(() => {
		if (!open) return;
		// Default to whatever's currently applied so CS can see at a glance.
		if (current_absolute > 0) {
			set_mode('absolute');
			set_absolute_value(String(current_absolute));
			set_selected(0);
			set_custom_value('');
		} else {
			set_mode('percentage');
			set_absolute_value('');
			const matching_preset = PRESETS.find(p => typeof p.value === 'number' && p.value === current_percentage);
			if (matching_preset) {
				set_selected(matching_preset.value);
				set_custom_value('');
			} else {
				set_selected('custom');
				set_custom_value(String(current_percentage));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const chosen_percentage = (() => {
		if (selected === 'custom') {
			const n = Number(custom_value);
			if (Number.isNaN(n) || custom_value === '') return null;
			return n;
		}
		if (selected === null) return null;
		return Number(selected);
	})();

	const chosen_absolute = (() => {
		const n = Number(absolute_value);
		if (Number.isNaN(n) || absolute_value === '') return null;
		return n;
	})();

	const valid_percentage = chosen_percentage !== null
		&& !Number.isNaN(chosen_percentage)
		&& chosen_percentage >= 0
		&& chosen_percentage <= 100;

	// Absolute TDS must be >= 0 and can't exceed the discounted, pre-tax amount
	// (mirrors the backend TOO_MUCH_TDS guard).
	const valid_absolute = chosen_absolute !== null
		&& !Number.isNaN(chosen_absolute)
		&& chosen_absolute >= 0
		&& chosen_absolute <= padbt;

	const is_valid = mode === 'absolute' ? valid_absolute : valid_percentage;

	// Client-side preview of the recompute. Mirrors backend pricing util:
	//   percentage: tds = price_after_discount_before_tax * pct / 100
	//   absolute:   tds = the entered amount
	//   total = (price_after_discount_before_tax - tds) + tax
	const preview = (() => {
		if (!due || !is_valid) return null;
		const tax_pct = padbt > 0 ? round2((Number(due.tax || 0) / padbt) * 100) : 0;
		const tax = round2((padbt * tax_pct) / 100);
		const new_tds = mode === 'absolute'
			? round2(chosen_absolute)
			: round2((padbt * chosen_percentage) / 100);
		const new_total = round2(padbt - new_tds + tax);
		return { padbt, tax, new_tds, new_total, tax_pct };
	})();

	const has_changed = mode === 'absolute'
		? (chosen_absolute !== null && (Number(chosen_absolute) !== current_absolute || current_percentage > 0))
		: (chosen_percentage !== null && (Number(chosen_percentage) !== current_percentage || current_absolute > 0));

	const submit = async () => {
		if (!due || !is_valid) return;
		try {
			set_submitting(true);
			set_page_loader(true, 'Updating TDS...');
			await general_fetch({
				url: 'csp/apply_tds_to_order',
				body: mode === 'absolute'
					? { order_id: due.id, tds_absolute: chosen_absolute }
					: { order_id: due.id, tds_percentage: chosen_percentage }
			});
			set_page_loader(false);
			set_submitting(false);
			toastr.success('TDS updated successfully');
			if (typeof on_success === 'function') on_success();
			toggle();
		} catch (err) {
			set_page_loader(false);
			set_submitting(false);
			console.error('apply_tds_to_order failed', err);
			const msg = err && err.err && err.err.message ? err.err.message : '';
			if (msg.includes('PENDING_OFFLINE_PAYMENT_BLOCKS_TDS_EDIT')) {
				toastr.error('Resolve the pending offline payment proof first.');
			} else if (msg.includes('TDS_ONLY_APPLICABLE_TO_INDIA_ORGS')) {
				toastr.error('TDS is only applicable to India market orgs.');
			} else if (msg.includes('INVALID_ORDER_TYPE_FOR_TDS')) {
				toastr.error('TDS can only be edited on renewal, overage, or mid-cycle add-on / license dues.');
			} else if (msg.includes('ORDER_NOT_IN_DUE_STATE')) {
				toastr.error('This order is not in a due state.');
			} else if (msg.includes('TOO_MUCH_TDS')) {
				toastr.error('TDS amount cannot exceed the order subtotal (after discount, before tax).');
			} else if (msg.includes('INVALID_TDS_ABSOLUTE') || msg.includes('INVALID_TDS_PARAMS')) {
				toastr.error('Enter a valid TDS amount.');
			} else {
				toastr.error('Could not update TDS, please try again.');
			}
		}
	};

	const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
	const currency_symbol = (store_info && store_info.country !== 'IN') ? '$' : '₹';

	const mode_btn_style = (active) => ({
		flex: 1,
		fontSize: 12,
		padding: '6px 8px',
		cursor: 'pointer',
		border: '1px solid ' + (active ? '#0958d9' : '#d6d9de'),
		background: active ? '#e8f1ff' : '#fff',
		color: active ? '#0958d9' : '#6b7280',
		fontWeight: active ? 700 : 500,
	});

	return (
		<Modal isOpen={open} toggle={toggle} className="modal-dialog-centered" size="md">
			<ModalHeader toggle={toggle}>Edit TDS for this Due</ModalHeader>
			<ModalBody style={{ fontSize: '14px' }}>
				<div className="mb-3" style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.5 }}>
					<i className="fa fa-info-circle mr-1" />
					TDS (Tax Deducted at Source) reduces the cash payable; the deducted amount
					is remitted by the client to the Income Tax department on our behalf. India-only.
					Picking 2% or 10% will surface a TDS line item on the proforma; custom rates and
					fixed amounts recompute the order total but won't add a Zoho TDS line.
				</div>

				<FormGroup>
					<label className="form-control-label">TDS Type</label>
					<div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden' }}>
						<div style={mode_btn_style(mode === 'percentage')} onClick={() => set_mode('percentage')}>Percentage (%)</div>
						<div style={mode_btn_style(mode === 'absolute')} onClick={() => set_mode('absolute')}>Fixed amount ({currency_symbol})</div>
					</div>
				</FormGroup>

				{mode === 'percentage' ? (
					<>
						<FormGroup>
							<label className="form-control-label">TDS Rate</label>
							<Input
								type="select"
								value={selected === null ? '' : selected}
								onChange={(e) => set_selected(e.target.value === 'custom' ? 'custom' : Number(e.target.value))}
							>
								<option value="" disabled>Select...</option>
								{PRESETS.map(p => (
									<option key={String(p.value)} value={p.value}>{p.label}</option>
								))}
							</Input>
						</FormGroup>
						{selected === 'custom' ? (
							<FormGroup>
								<label className="form-control-label">Custom percentage (0-100)</label>
								<Input
									type="number"
									min="0"
									max="100"
									step="0.01"
									value={custom_value}
									onChange={(e) => set_custom_value(e.target.value)}
									placeholder="e.g. 5"
								/>
							</FormGroup>
						) : null}
					</>
				) : (
					<FormGroup>
						<label className="form-control-label">TDS amount ({currency_symbol})</label>
						<Input
							type="number"
							min="0"
							step="0.01"
							value={absolute_value}
							onChange={(e) => set_absolute_value(e.target.value)}
							placeholder={`e.g. 5000`}
						/>
						{chosen_absolute !== null && chosen_absolute > padbt ? (
							<div style={{ fontSize: 11, color: '#cf1322', marginTop: 4 }}>
								Amount can't exceed the subtotal ({currency_symbol}{fmt(padbt)}).
							</div>
						) : null}
					</FormGroup>
				)}

				{preview ? (
					<div style={{ background: '#fafbfc', border: '1px solid #eef0f2', borderRadius: 6, padding: '10px 12px', marginTop: 8 }}>
						<div style={{ fontSize: 11, fontWeight: 700, color: '#1f2933', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
							New breakup
						</div>
						<table style={{ width: '100%', fontSize: 13 }}>
							<tbody>
								<tr><td style={{ color: '#6b7280' }}>Subtotal (after discount)</td><td style={{ textAlign: 'right' }}>{currency_symbol}{fmt(preview.padbt)}</td></tr>
								<tr><td style={{ color: '#6b7280' }}>Tax ({preview.tax_pct}%)</td><td style={{ textAlign: 'right' }}>+ {currency_symbol}{fmt(preview.tax)}</td></tr>
								<tr><td style={{ color: '#6b7280' }}>TDS {mode === 'absolute' ? '(fixed)' : `(${chosen_percentage}%)`}</td><td style={{ textAlign: 'right', color: '#cf1322' }}>− {currency_symbol}{fmt(preview.new_tds)}</td></tr>
								<tr style={{ borderTop: '1px solid #eef0f2' }}>
									<td style={{ fontWeight: 700, paddingTop: 4 }}>New total</td>
									<td style={{ textAlign: 'right', fontWeight: 700, paddingTop: 4 }}>{currency_symbol}{fmt(preview.new_total)}</td>
								</tr>
							</tbody>
						</table>
					</div>
				) : null}
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" size="sm" onClick={toggle} disabled={submitting}>Cancel</Button>
				<Button
					color="primary" size="sm"
					onClick={submit}
					disabled={submitting || !is_valid || !has_changed}
				>
					{submitting ? 'Updating...' : 'Apply TDS'}
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default EditTdsModal;
